import { useLang } from 'hooks/lang';

import { FullProject, loaders } from 'entities';

import { Card, Column, Label, Skeleton, Button, Row } from 'components/Blocks';
import { EntityStatusTag } from 'components/Entities';

import {
  CircleSlashIcon,
  ExternalLinkIcon,
  HalfFilledCircleArrowIcon,
  WavesIcon,
  WorkflowIcon,
} from 'components/Icons';

import { SingleProperty } from '../SingleProperty';

interface ProjectTypeInfoProps {
  project: FullProject;
}

function TrackerOrigin({ project }: ProjectTypeInfoProps): JSX.Element {
  const { t } = useLang();

  return (
    <div className="grid grid-cols-auto-200 gap-4">
      <SingleProperty
        valueClassName="border-0 font-medium"
        Icon={WavesIcon}
        value={project.waves}
        label={t('number-of-waves')}
      />

      <SingleProperty
        valueClassName="border-0 font-medium"
        Icon={HalfFilledCircleArrowIcon}
        value={project.wavesLeft}
        label={t('waves-left')}
      />

      <SingleProperty
        valueClassName="border-0 font-medium"
        Icon={CircleSlashIcon}
        value={project.shadowBanInWaves}
        label={`${t('exclude-time')} (${t('in-waves')})`}
      />
    </div>
  );
}

function TrackerSequence({ project }: ProjectTypeInfoProps): JSX.Element {
  const { data, errorOrLoading } = loaders.projects.fullProject.use({
    params: { id: project.originProject! },
  });

  if (errorOrLoading) return <Skeleton height={100} />;

  return (
    <Column className="rounded-md border bg-foreground/5 p-3">
      <Label>
        <EntityStatusTag status={data!.status} target="project" />
        {data!.name}
      </Label>

      <TrackerOrigin project={data!} />
    </Column>
  );
}

const relatedInfoByType: Record<string, (typeInfo: ProjectTypeInfoProps) => JSX.Element> = {
  TRACKER_SEQUENCE: TrackerSequence,
  TRACKER_ORIGIN: TrackerOrigin,
};

export function ProjectTypeInfo({ project }: ProjectTypeInfoProps) {
  const { t } = useLang();

  const RelatedInfo = relatedInfoByType[project.type];

  if (!RelatedInfo) return null;

  return (
    <Card flow="column">
      <Row>
        <Label>
          <WorkflowIcon />
          {t('tracker-info')}
        </Label>

        {project.originProject && project.type === 'TRACKER_SEQUENCE' && (
          <Button
            to="projectProfile"
            params={{ projectId: project.originProject! }}
            variant="ghost"
            className="ml-auto w-fit text-sm"
            size="small"
          >
            {t('access-origin')}
            <ExternalLinkIcon size={14} />
          </Button>
        )}
      </Row>

      <RelatedInfo project={project} />
    </Card>
  );
}
